/**
 * Harvest Monitoring Page
 * Main page component for harvest monitoring
 */

import React, { useEffect } from 'react';
import {
    Box,
    HStack,
    Heading,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import Navbar from '../../../components/Navbar';
import { FilterHarvestMonitoring } from '../components/FilterHarvestMonitoring';
import { HarvestMonitoringTable } from '../components/HarvestMonitoringTable';
import { useHarvestMonitoring } from '../hooks/useHarvestMonitoring';
import { exportSpbToExcel } from '../exportSpbExcel';
import type { HarvestMonitoringFilters } from '../types';

export const HarvestMonitoringPage: React.FC = () => {
    const { data, spbData, viewMode, loading, error, filters, fetchData } = useHarvestMonitoring();
    const toast = useToast();

    // Fetch data on initial load
    useEffect(() => {
        fetchData();
    }, []);

    // Show error toast when error occurs
    useEffect(() => {
        if (error) {
            toast({
                title: 'Error',
                description: error,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        }
    }, [error, toast]);

    const handleFiltersChange = (filters: HarvestMonitoringFilters, mode?: 'block' | 'spb') => {
        fetchData(filters, mode);
    };

    const handleDownloadExcel = () => {
        const start = filters.start_date ?? filters.current_date;
        const end = filters.end_date ?? start;
        const toDisplay = (ymd: string) => `${ymd.slice(6, 8)}/${ymd.slice(4, 6)}/${ymd.slice(0, 4)}`;
        exportSpbToExcel(
            spbData,
            `Monitoring Panen per SPB: ${toDisplay(start)} - ${toDisplay(end)}`,
            `monitoring-panen-spb-${start}-${end}.xlsx`,
        );
    };

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />

            <Box flex="1" p={{ base: 4, md: 6 }}>
                <VStack spacing={6} align="stretch">
                    <VStack align="start" spacing={1}>
                        <Heading size="lg" color="gray.700" mb={2}>
                            Monitoring Panen
                        </Heading>
                        <HStack spacing={4} flexWrap="wrap">
                            <Text color="gray.600" fontSize="sm">
                                Data monitoring panen per rentang tanggal (maks. 31 hari)
                            </Text>
                        </HStack>
                    </VStack>

                    <FilterHarvestMonitoring
                        onFiltersChange={handleFiltersChange}
                        isLoading={loading}
                        viewMode={viewMode}
                        showGroupBy={true}
                        isDateRange={true}
                        onDownloadExcel={viewMode === 'spb' ? handleDownloadExcel : undefined}
                        isExcelDisabled={spbData.length === 0}
                    />

                    <HarvestMonitoringTable
                        data={data}
                        spbData={spbData}
                        viewMode={viewMode}
                        loading={loading}
                        error={error}
                    />
                </VStack>
            </Box>
        </Box>
    );
};
